import {SectionMessage} from "@vesta/aergia/components/section-message";
import {notifications} from "@mantine/notifications";
import type {Integer, Iso8601Date, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {DateTime} from "luxon";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {PaymentCurrency} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentCurrency";
import type {SubscriptionPlanPricing} from "~/domainModels/subscriptionPlans";
import {PaymentFrequency} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentFrequency";
import type {OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import type {TagColor} from "@atlaskit/tag";
import {paymentCurrencyToDetailsMap} from "~/utilities/paymentDetails";
import {normalizeCreatedUpdatedByUserIdForDisplay} from "~/utilities/deprecatedSystemGeneratedCreatedUpdatedByUserIds";
import type {UserDetails} from "@vesta/vos-types/modules/platformLevel/userDetails/domain/UserDetails";
import {valueToText} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";

declare global {
    interface Window {
        dataLayer?: Array<Record<string, unknown>>;
    }
}

export function dispositionToLevelFormat(flatDisposition: string): string {
    const toTitleCase = (segment: string): string =>
        segment
            .split("-")
            .filter((word) => word !== "")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");

    const segments = flatDisposition.split("_").filter((segment) => segment.trim() !== "");
    if (segments.length === 0) {
        return flatDisposition;
    }

    return segments.map(toTitleCase).join(" | ");
}

export function getHoursLeftInDay(): number {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();

    // Time left until midnight
    const remainingHours = 23 - hours + (1 - minutes / 60);
    return Math.max(0, Math.round(remainingHours)); // ensure non-negative integer
}

function addCommasToNumber(number: number, system: NumberSystems) {
    if (system === NumberSystems.international) {
        return number.toLocaleString("en-US");
    } else {
        return number.toLocaleString("en-IN");
    }
}

export function numberToHumanFriendlyString(n: number) {
    if (n == null || isNaN(n)) {
        return "?";
    }

    if (n < 10 ** 3) {
        return n.toFixed(2);
    }

    if (n <= 10 ** 5) {
        return `${(n / 10 ** 3).toFixed(2)}K`;
    } else if (n < 10 ** 8) {
        return `${(n / 10 ** 6).toFixed(2)}M`;
    } else {
        return `${(n / 10 ** 9).toFixed(2)}B`;
    }
}

export function numberToHumanFriendlyStringWithNumberSystem(n: number, numberSystem: NumberSystems, toFixed: number = 2, shouldShortenValue: boolean = true) {
    if (n == null || isNaN(n)) {
        return "?";
    }

    if (numberSystem === NumberSystems.international || numberSystem === NumberSystems.none) {
        if (n < 10 ** 3) {
            return n.toFixed(toFixed);
        }

        if (shouldShortenValue === true) {
            if (n <= 10 ** 5) {
                return `${(n / 10 ** 3).toFixed(toFixed)}K`;
            } else if (n < 10 ** 8) {
                return `${(n / 10 ** 6).toFixed(toFixed)}M`;
            } else {
                return `${(n / 10 ** 9).toFixed(toFixed)}B`;
            }
        } else {
            return addCommasToNumber(parseFloat(n.toFixed(toFixed)), numberSystem);
        }
    } else if (numberSystem === NumberSystems.indian) {
        if (n < 10 ** 3) {
            return n.toFixed(toFixed);
        }

        if (shouldShortenValue === true) {
            if (n < 10 ** 5) {
                return `${(n / 10 ** 3).toFixed(toFixed)}K`;
            } else if (n < 10 ** 7) {
                return `${(n / 10 ** 5).toFixed(toFixed)}L`;
            } else {
                return `${(n / 10 ** 7).toFixed(toFixed)}Cr`;
            }
        } else {
            return addCommasToNumber(parseFloat(n.toFixed(toFixed)), numberSystem);
        }
    } else {
        return n.toFixed(toFixed);
    }
}
/**
 * Converts an ISO date string (e.g., "2025-05-05T17:49:58.395Z")
 * into a human-readable date format (e.g., "15/12/2024").
 *
 * @param isoDate - The ISO 8601 date string to format.
 * @returns A formatted date string in DD/MM/YYYY format.
 *          Returns "Invalid Date" if input is not a valid ISO date.
 */
export function formatIsoToDDMMYYYY(isoDate: string): string {
    if (!isoDate || typeof isoDate !== "string") {
        return "Invalid Date";
    }

    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
        return "Invalid Date";
    }

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
}

export function showSuccessToast(title: string, message: string, id?: string) {
    notifications.show({
        title: title,
        message: message,
        color: "green",
        id: id,
    });
}

export function showInfoToast(title: string, message: string, id?: string) {
    notifications.show({
        title: title,
        message: message,
        color: "blue",
        id: id,
    });
}

export function showWarningToast(title: string, message: string, id?: string) {
    notifications.show({
        title: title,
        message: message,
        color: "yellow",
        id: id,
    });
}

export function showErrorToast(title: string, message: string, id?: string) {
    notifications.show({
        title: title,
        message: message,
        color: "red",
        id: id,
    });
}

export function showSectionMessageToast({
    appearance,
    title,
    message,
    id,
    actions,
}: {
    appearance?: "information" | "warning" | "error" | "success" | "discovery";
    title?: string;
    message?: string;
    id?: string;
    actions?: React.ReactElement | React.ReactElement[];
}) {
    notifications.show({
        id,
        autoClose: 3000,
        color: "transparent",
        withCloseButton: false,
        message: (
            <SectionMessage
                appearance={appearance}
                title={title}
                isDismissible
                actions={actions}
                alignText="center"
            >
                {message}
            </SectionMessage>
        ),
        styles: {
            root: {
                background: "transparent",
                padding: 0,
                boxShadow: "none",
            },
        },
    });
}

export function getFormattedDateDiff(date1: DateTime, date2: DateTime): string {
    const diff = date1.diff(date2, ["years", "months", "days", "hours", "minutes", "seconds"]).toObject();

    if (diff.days === 0 && diff.months === 0 && diff.years === 0 && diff.hours === 0 && diff.minutes === 0 && diff.seconds !== undefined) {
        return `${Math.floor(diff.seconds)} ${Math.floor(diff.seconds) === 1 ? `second` : `seconds`} ago`;
    }

    if (diff.days === 0 && diff.months === 0 && diff.years === 0 && diff.hours === 0 && diff.minutes !== undefined) {
        return `${Math.floor(diff.minutes)} ${Math.floor(diff.minutes) === 1 ? `minute` : `minutes`} ago`;
    }

    if (diff.days === 0 && diff.months === 0 && diff.years === 0 && diff.hours !== undefined) {
        return `${Math.floor(diff.hours)} ${Math.floor(diff.hours) === 1 ? `hour` : `hours`} ago`;
    }

    if (diff.months === 0 && diff.years === 0 && diff.days !== undefined) {
        return `${Math.floor(diff.days)} ${Math.floor(diff.days) === 1 ? `day` : `days`} ago`;
    }

    if (diff.years === 0 && diff.months !== undefined) {
        return `${Math.floor(diff.months)} ${Math.floor(diff.months) === 1 ? `month` : `months`} ago`;
    }

    if (diff.years !== undefined) {
        return `${Math.floor(diff.years)} ${Math.floor(diff.years) === 1 ? `year` : `years`} ago`;
    }

    return `A long time ago`;
}

const ottopilotDisplayDateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    month: "short",
    timeZone: "Asia/Kolkata",
    year: "numeric",
});

/**
 * Sample output: 16 Jul 2026, 17:40 (Asia/Kolkata, 24-hour clock)
 */
export function formatOttopilotDisplayDateTime(isoString: string): string {
    if (isoString === null || isoString === undefined || isoString === "") {
        return "-";
    }
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime()) === true) {
        return "-";
    }
    return ottopilotDisplayDateTimeFormatter.format(date);
}

/**
 * Sample output: 16 Jul 2026, 17:40 (Asia/Kolkata, 24-hour clock)
 */
export function formatDateTime(isoString: string): string {
    return formatOttopilotDisplayDateTime(isoString);
}

export function formatIsoDateTimeCompact(isoString: string): string {
    return formatOttopilotDisplayDateTime(isoString);
}


export function getUserRole(roleId: Uuid | null, userTemplates: Array<UserTemplate>): string {
    if (roleId === null) {
        return "-";
    }

    const userTemplate = userTemplateById(userTemplates).get(roleId);
    if (userTemplate === undefined) {
        return "?";
    }
    return userTemplate.displayName;
}

const userTemplateByIdCache = new WeakMap<Array<UserTemplate>, Map<string, UserTemplate>>();

function userTemplateById(userTemplates: Array<UserTemplate>): Map<string, UserTemplate> {
    const cached = userTemplateByIdCache.get(userTemplates);
    if (cached !== undefined) {
        return cached;
    }
    const index = new Map(userTemplates.map((userTemplate) => [userTemplate.id, userTemplate]));
    userTemplateByIdCache.set(userTemplates, index);
    return index;
}

export function findWorkspaceUserByStoredReferenceId(
    storedUserReferenceId: string,
    users: Array<User>,
): User | undefined {
    const trimmed = String(storedUserReferenceId).trim();
    if (trimmed === "") {
        return undefined;
    }
    return workspaceUserByReferenceId(users).get(trimmed.toLowerCase());
}

const workspaceUserIndexCache = new WeakMap<Array<User>, Map<string, User>>();

function workspaceUserByReferenceId(users: Array<User>): Map<string, User> {
    const cached = workspaceUserIndexCache.get(users);
    if (cached !== undefined) {
        return cached;
    }
    const index = new Map<string, User>();
    for (const user of users) {
        index.set(String(user.userId).toLowerCase(), user);
    }
    workspaceUserIndexCache.set(users, index);
    return index;
}

export type PlatformUserDisplayDetails = Pick<UserDetails, "email" | "name" | "userId">;

export function getPlatformUserDisplayName(platformUser: PlatformUserDisplayDetails | null | undefined): string | null {
    const name = platformUser?.name?.trim();
    if (name !== undefined && name !== "") {
        return name;
    }

    const email = platformUser?.email?.trim();
    if (email !== undefined && email !== "") {
        return email;
    }

    return null;
}

/**
 * Returns the displayName for a workspace user.
 * Returns `-` if userId is null or the user / user template could not be resolved.
 */
export function getUserDisplayName(
    userId: Uuid | null,
    users: Array<User>,
    userTemplates: Array<UserTemplate>,
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>,
): string {
    const normalizedUserId = normalizeCreatedUpdatedByUserIdForDisplay(userId);
    if (normalizedUserId === null) {
        return "-";
    }

    const trimmed = String(normalizedUserId).trim();
    if (trimmed === "") {
        return "-";
    }

    if (platformUserDetailsByUserId !== undefined) {
        const platformDisplayNameFromDirectReference = getPlatformUserDisplayName(platformUserDetailsByUserId.get(trimmed as Uuid));
        if (platformDisplayNameFromDirectReference !== null) {
            return platformDisplayNameFromDirectReference;
        }
    }

    const user = findWorkspaceUserByStoredReferenceId(trimmed, users);
    if (user === undefined) {
        return "-";
    }

    if (platformUserDetailsByUserId !== undefined) {
        const platformDisplayName = getPlatformUserDisplayName(platformUserDetailsByUserId.get(user.userId));
        return platformDisplayName ?? "-";
    }

    const userTemplate = userTemplateById(userTemplates).get(user.templateId);
    if (userTemplate === undefined) {
        return user.userId;
    }

    // User names now live on platform user details. Keep this template path around for future
    // configurable user labels, but do not render it today because migrated identity columns were
    // intentionally removed from additionalColumnValues.
    // return processOttopilotStringTemplate(userTemplate.instanceDisplayNameTemplate, user.additionalColumnValues);
    return user.userId;
}

export function isStringArray(value: Array<string | number>): value is string[] {
    return Array.isArray(value) && value.every((item) => typeof item === "string");
}

export function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export const isClickInBox = (clickX: number, clickY: number, boxTop: number, boxRight: number, boxBottom: number, boxLeft: number): boolean => {
    if (boxLeft <= clickX && clickX <= boxRight && boxTop <= clickY && clickY <= boxBottom) {
        return true;
    }

    return false;
};

export async function convertCanvasToJpgFile(canvas: HTMLCanvasElement, quality: number, fileName: string): Promise<File> {
    return new Promise((resolve) => {
        canvas.toBlob(
            (blob) => {
                if (blob === null) {
                    return;
                }

                const file = new File([blob], fileName, {
                    type: "image/jpeg",
                });

                resolve(file);
            },
            "image/jpeg",
            quality,
        );
    });
}

export function convertCanvasToDataUrl(canvas: HTMLCanvasElement): string {
    return canvas.toDataURL("image/jpg");
}

export function generateDatesBetweenRange(minDate: Iso8601DateTime, maxDate: Iso8601DateTime) {
    const dates = [];

    let date = DateTime.fromISO(minDate, {
        setZone: true,
    }).toISODate() as Iso8601Date;
    const maxDateIso = DateTime.fromISO(maxDate, {
        setZone: true,
    }).toISODate() as Iso8601Date;
    while (date <= maxDateIso) {
        dates.push(date);
        date = DateTime.fromISO(date).plus({days: 1}).toISODate() as Iso8601Date;
    }

    return dates;
}

export function formatCurrencyAmount(amount: Integer, currency: PaymentCurrency): string {
    if (currency === PaymentCurrency.inr) {
        const amountStr = amount.toString();
        // TODO: Do this properly
        if (amountStr.length < 4) {
            return `${paymentCurrencyToDetailsMap[currency].symbol}${amountStr}`;
        } else if (amountStr.length < 6) {
            return `${paymentCurrencyToDetailsMap[currency].symbol}${amountStr.slice(0, amountStr.length - 3)},${amountStr.slice(amountStr.length - 3)}`;
        } else {
            return `${paymentCurrencyToDetailsMap[currency].symbol}${amountStr.slice(0, amountStr.length - 5)},${amountStr.slice(
                amountStr.length - 5,
                amountStr.length - 3,
            )},${amountStr.slice(amountStr.length - 3)}`;
        }
    } else if (currency === PaymentCurrency.usd) {
        const amountStr = amount.toString();
        // TODO: Do this properly
        if (amountStr.length < 4) {
            return `${paymentCurrencyToDetailsMap[currency].symbol}${amountStr}`;
        } else if (amountStr.length < 7) {
            return `${paymentCurrencyToDetailsMap[currency].symbol}${amountStr.slice(0, amountStr.length - 3)},${amountStr.slice(amountStr.length - 3)}`;
        } else {
            return `${paymentCurrencyToDetailsMap[currency].symbol}${amountStr.slice(0, amountStr.length - 6)},${amountStr.slice(
                amountStr.length - 6,
                amountStr.length - 3,
            )},${amountStr.slice(amountStr.length - 3)}`;
        }
    } else {
        throw new Error("Unexpected currency");
    }
}

export function getAmountFromPricingObject(pricingObject: SubscriptionPlanPricing | null, frequency: PaymentFrequency, marketPrice: boolean = false): Integer {
    if (pricingObject === null) {
        return 0 as Integer;
    }
    const baseAmount = marketPrice ? pricingObject.mrp : pricingObject.crp;

    const frequencyMultipliers: Record<PaymentFrequency, number> = {
        [PaymentFrequency.monthly]: 1,
        [PaymentFrequency.yearly]: 12,
        [PaymentFrequency.weekly]: 7 / 28,
        [PaymentFrequency.everyTwoDays]: 2 / 28,
        [PaymentFrequency.everyThreeDays]: 3 / 28,
        [PaymentFrequency.everyFourDays]: 4 / 28,
    };

    const multiplier = frequencyMultipliers[frequency];

    const computedAmount = baseAmount * multiplier;
    const finalAmount = Math.floor(computedAmount);

    return finalAmount as Integer;
}

export function getOrdinalSuffix(day: number) {
    if (day % 10 === 1 && day % 100 !== 11) {
        return "st";
    }
    if (day % 10 === 2 && day % 100 !== 12) {
        return "nd";
    }
    if (day % 10 === 3 && day % 100 !== 13) {
        return "rd";
    }
    return "th";
}

export function sendGtmEvent(
    event: string,
    // payload: any
) {
    if (window === undefined) {
        return;
    }

    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({event: event});
}


/**
 * Converts an object to readable text (key: value lines) or copies a string as-is, then copies to clipboard.
 */
export async function copyDataToClipboard<T extends Record<string, unknown> | string>(data: T, transformer?: (key: string) => string): Promise<void> {
    const text =
        typeof data === "string"
            ? data
            : (() => {
                  const lines = Object.entries(data).map(([key, value]) => {
                      const str = valueToText(value);
                      const displayKey = transformer !== undefined ? transformer(key) : key;
                      return `${displayKey}: ${str}`;
                  });
                  return lines.join("\n");
              })();
    await navigator.clipboard.writeText(text);
}

/** Download without letting the SPA router treat the temporary `<a>` as in-app navigation. */
function triggerBlobDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Converts a list of objects to a CSV (header row + one row per object) and triggers a download.
 * Values are stringified; nested objects are JSON.stringify'd.
 */
export function exportRowsAsCsv<T extends Record<string, unknown>>(rows: Array<T>, filename?: string, transformer?: (key: string) => string): void {
    if (rows.length === 0) {
        return;
    }

    const keys = Object.keys(rows[0]!) as Array<keyof T>;

    const escapeCsvCell = (value: unknown): string => {
        const str = valueToText(value);
        const needsQuotes = str.includes(",") || str.includes('"') || str.includes("\n");
        return needsQuotes ? `"${str.replaceAll('"', '""')}"` : str;
    };

    const headerRow = keys.map((k) => escapeCsvCell(transformer !== undefined ? transformer(String(k)) : String(k))).join(",");

    const dataRows = rows.map((row) => keys.map((k) => escapeCsvCell(row[k])).join(",")).join("\n");

    const csv = `${headerRow}\n${dataRows}`;

    const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
    triggerBlobDownload(blob, filename !== undefined && filename !== "" ? filename : "export.csv");
}

/** Matches `isTableEmptyCellValue` in tableUtils — unresolved placeholders export as blank, not "?". */
function isExportEmptyCellValue(value: unknown): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed === "" || trimmed === "?";
    }
    return false;
}

/**
 * Transforms a row into a flat record of human-readable display names and formatted values using lineItems.
 * If the raw value is null, undefined, blank, or "?", the cell is exported as "-" (no formatter is invoked),
 * avoiding "?" from user-lookup formatters or "null"/"undefined" from String() coercion.
 */
function resolveExportCell<T>(row: T, column: OttopilotColumn<T>): string {
    const exportColumn = column as OttopilotColumn<T> & {
        valueFormatter?: (value: unknown) => unknown;
        valueGetter: (value: T) => unknown;
    };
    const rawValue = exportColumn.valueGetter(row);

    // Do not call formatters with null/undefined. User lookup formatters can turn
    // missing values into "?", which would misrepresent an empty database value.
    if (rawValue === null || rawValue === undefined) {
        return "-";
    }

    const formattedValue = exportColumn.valueFormatter?.(rawValue) ?? rawValue;
    if (isExportEmptyCellValue(formattedValue)) {
        return "-";
    }

    const stringValue = Array.isArray(formattedValue) ? formattedValue.join(", ") : valueToText(formattedValue);
    return isExportEmptyCellValue(stringValue) ? "-" : stringValue;
}

/**
 * Resolves cells in column order. Unlike a display-name keyed object, this
 * preserves distinct values when two configured fields share the same label.
 */
export function transformRowToExportCells<T>(
    row: T,
    lineItems: Array<OttopilotColumn<T>>,
): Array<string> {
    return lineItems.map((column) => resolveExportCell(row, column));
}

export function transformRowForExport<T>(row: T, lineItems: Array<OttopilotColumn<T>>): Record<string, string> {
    const dataToExport: Record<string, string> = {};
    const cells = transformRowToExportCells(row, lineItems);
    lineItems.forEach((column, index) => {
        dataToExport[column.displayName] = cells[index] ?? "-";
    });
    return dataToExport;
}

export function formatDateAndTime(isoString: string): {date: string; time: string} {
    if (isoString === "") {
        return {date: "-", time: "-"};
    }
    const dt = DateTime.fromISO(isoString, {zone: "Asia/Kolkata"});
    if (!dt.isValid) {
        return {date: "-", time: "-"};
    }
    return {date: dt.toFormat("d LLL yyyy"), time: dt.toFormat("HH:mm")};
}

export function formatTime12Hour(isoString: string): string {
    if (isoString === "") {
        return "-";
    }
    const dt = DateTime.fromISO(isoString, {zone: "Asia/Kolkata"});
    if (!dt.isValid) {
        return "-";
    }
    return dt.toFormat("h:mm a");
}

export function toIso8601FromIstSafe(dateTime: string): string | null {
    const dt = DateTime.fromFormat(dateTime, "yyyy-MM-dd HH:mm:ss", {zone: "Asia/Kolkata"});
    if (!dt.isValid) {
        return null;
    }

    return dt.toUTC().toISO({suppressMilliseconds: true});
}

export function coercePickerDateTime(value: unknown): Date | null {
    if (value === null || value === undefined) {
        return null;
    }
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return null;
        }
        return value;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") {
            return null;
        }
        const parsed = new Date(trimmed);
        if (Number.isNaN(parsed.getTime())) {
            return null;
        }
        return parsed;
    }
    if (typeof value === "number") {
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return null;
        }
        return parsed;
    }
    if (typeof value === "object") {
        const maybeDayJs = value as {$d?: unknown; toDate?: () => unknown};
        if (typeof maybeDayJs.toDate === "function") {
            const inner = maybeDayJs.toDate();
            if (inner instanceof Date && Number.isNaN(inner.getTime()) === false) {
                return inner;
            }
        }
        if (maybeDayJs.$d instanceof Date && Number.isNaN(maybeDayJs.$d.getTime()) === false) {
            return maybeDayJs.$d;
        }
    }
    return null;
}

export function pickerInstantToUtcIso8601(value: Date): string {
    return value.toISOString().replace(/\.\d{3}Z$/, "Z");
}

export function schedulePickerOrLegacyToUtcIso(value: unknown): string | null {
    const asDate = coercePickerDateTime(value);
    if (asDate !== null) {
        return pickerInstantToUtcIso8601(asDate);
    }
    if (typeof value !== "string") {
        return null;
    }
    const trimmed = value.trim();
    if (trimmed === "") {
        return null;
    }
    const fromIstWall = toIso8601FromIstSafe(trimmed);
    if (fromIstWall !== null) {
        return fromIstWall;
    }
    return null;
}

/**
 * Sample output: 12 Dec 2025
 */
export function formatDate(isoString: string): string {
    if (isoString === "") {
        return "-";
    }
    const dt = DateTime.fromISO(isoString, {zone: "Asia/Kolkata"});
    if (!dt.isValid) {
        return "-";
    }
    return dt.toFormat("d LLL yyyy");
}

/** Same formula as `deterministicShuffle` in global-common-typescript — deterministic [0, 1) from an integer seed. */
export function seededRandom(seed: number): number {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
}

const tagColors: Array<TagColor> = [
    "standard",
    "blue",
    "green",
    "teal",
    "purple",
    "red",
    "yellow",
    "orange",
    "magenta",
    "lime",
    "grey",
    "greenLight",
    "tealLight",
    "blueLight",
    "purpleLight",
    "redLight",
    "yellowLight",
    "orangeLight",
    "magentaLight",
    "limeLight",
    "greyLight",
];

export function getRandomTagColor(seed: number): TagColor {
    return tagColors[Math.floor(seededRandom(seed) * tagColors.length)];
}

/**
 * Turns a flat disposition key (underscore-separated path segments, hyphens inside segments)
 * into title-case words with spaces, similar to curated `displayName` strings.
 *
 * Example: `connected_invalid-lead_filled-by-mistake` → `Connected Invalid Lead Filled By Mistake`
 */
export function flatDispositionToDisplayNameFormat(flatDisposition: string): string {
    const trimmed = flatDisposition.trim();
    if (trimmed === "") {
        return flatDisposition;
    }

    const titleCaseToken = (token: string): string => {
        if (token === "") {
            return "";
        }
        const first = token[0];
        if (first === undefined) {
            return "";
        }
        return first.toUpperCase() + token.slice(1).toLowerCase();
    };

    const segmentStrings: Array<string> = [];
    const segments = trimmed.split("_");
    for (const segment of segments) {
        if (segment === "") {
            continue;
        }
        const hyphenParts = segment.split("-");
        const words: Array<string> = [];
        for (const part of hyphenParts) {
            if (part === "") {
                continue;
            }
            words.push(titleCaseToken(part));
        }
        if (words.length > 0) {
            segmentStrings.push(words.join(" "));
        }
    }

    if (segmentStrings.length === 0) {
        return flatDisposition;
    }

    return segmentStrings.join(" ");
}

/**
 * @param min Inclusive
 * @param max Exclusive
 */
export function getRandomInteger(min: Integer, max: Integer): Integer {
    const range = max - min;
    return (min + Math.floor(Math.random() * range)) as Integer;
}

/**
 * Used to tag a string so that we can automatically detect uniqueness across the codebase later.
 */
export function uniqueString(strings: TemplateStringsArray): string {
    return strings.join();
}

/**
 * Used to tag a string so that we can automatically detect uniqueness across the codebase later.
 */
export function uniqueUuid(strings: TemplateStringsArray): Uuid {
    return strings.join() as Uuid;
}

export function applyTableSelectionToLoadedRows<T extends {id: Uuid}>(loadedRows: T[], selectedTableRowIds: ReadonlyArray<string>): T[] {
    if (selectedTableRowIds.length === 0) {
        return loadedRows;
    }
    const selected = new Set(selectedTableRowIds);
    return loadedRows.filter((row) => selected.has(row.id));
}

export function getInitials(name: string): string {
    if (!name) {
        return "";
    }
    return name
        .split(" ")
        .filter((part) => part.length > 0)
        .map((part) => part[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
}

export function generateInitialsAvatarDataUrl(name: string): string {
    const initials = getInitials(name);
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = Math.abs(hash) % 360;
    const color = `hsl(${h}, 65%, 45%)`;

    const svg = `
<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <rect width="100" height="100" fill="${color}"/>
  <text x="50" y="50" dominant-baseline="central" text-anchor="middle" font-family="Inter, sans-serif" font-size="40" font-weight="600" fill="white">${initials}</text>
</svg>
    `.trim();

    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export function formatDateFromJsDate(date: Date): string {
    if (Number.isNaN(date.getTime())) {
        return "-";
    }
    return DateTime.fromJSDate(date, {zone: "Asia/Kolkata"}).toFormat("d LLL yyyy");
}

export function formatDateTimeFromJsDate(date: Date): string {
    if (Number.isNaN(date.getTime())) {
        return "-";
    }
    return DateTime.fromJSDate(date, {zone: "Asia/Kolkata"}).toFormat("d LLL yyyy, HH:mm");
}

export function formatTimeFromJsDate(date: Date): string {
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    return DateTime.fromJSDate(date, {zone: "Asia/Kolkata"}).toFormat("HH:mm");
}
